import React, { useState } from 'react';
import {
  Clock,
  Sun,
  Moon,
  Sunrise,
  Sunset,
  Settings,
  Bell,
  Shield,
  MapPin,
  Smartphone,
  Monitor,
  Fingerprint,
  Save,
  Plus,
  Edit2,
  Trash2,
  X,
  Check,
  ToggleLeft,
  ToggleRight,
  AlertCircle,
} from 'lucide-react';
import { SHIFTS, DEPARTMENTS } from './attendanceData';
import toast from 'react-hot-toast';
import { useAuth } from "@/context/AuthContext";
import { P } from "@/config/permissionCodes";

// Toggle Switch Component
const ToggleSwitch = ({ enabled, onChange, label, description }) => {
  return (
    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
      <div>
        <p className="font-medium text-gray-900">{label}</p>
        {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
      </div>
      <button
        onClick={() => onChange(!enabled)}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
          enabled ? 'bg-blue-600' : 'bg-gray-300'
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            enabled ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  );
};

// Number Input with Label
const NumberInput = ({ label, description, value, onChange, min, max, suffix }) => {
  return (
    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
      <div>
        <p className="font-medium text-gray-900">{label}</p>
        {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
      </div>
      <div className="flex items-center gap-2">
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(parseInt(e.target.value) || 0)}
          min={min}
          max={max}
          className="w-20 px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-center focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        {suffix && <span className="text-sm text-gray-500">{suffix}</span>}
      </div>
    </div>
  );
};

// Shift Card Component
const ShiftCard = ({ shift, onEdit, onDelete, onToggle }) => {
  const icons = {
    Sunrise: Sunrise,
    Sun: Sun,
    Sunset: Sunset,
    Moon: Moon,
  };

  const Icon = icons[shift.icon] || Sun;

  const colorClasses = {
    amber: 'bg-amber-100 text-amber-600',
    blue: 'bg-blue-100 text-blue-600',
    orange: 'bg-orange-100 text-orange-600',
    indigo: 'bg-indigo-100 text-indigo-600',
  };

  return (
    <div className={`bg-white rounded-lg border ${shift.active ? 'border-gray-200' : 'border-gray-100 opacity-60'} p-4`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 ${colorClasses[shift.color] || 'bg-gray-100 text-gray-600'} rounded-lg flex items-center justify-center`}>
            <Icon size={20} />
          </div>
          <div>
            <p className="font-semibold text-gray-900">{shift.name}</p>
            <p className="text-xs text-gray-500">{shift.code}</p>
          </div>
        </div>
        <button
          onClick={() => onToggle(shift.id)}
          className={`p-1 rounded transition ${shift.active ? 'text-emerald-600' : 'text-gray-400'}`}
        >
          {shift.active ? <ToggleRight size={24} /> : <ToggleLeft size={24} />}
        </button>
      </div>

      <div className="space-y-2 text-sm">
        <div className="flex items-center justify-between">
          <span className="text-gray-500">Start Time</span>
          <span className="font-medium text-gray-900">{shift.startTime}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-gray-500">End Time</span>
          <span className="font-medium text-gray-900">{shift.endTime}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-gray-500">Duration</span>
          <span className="font-medium text-gray-900">{shift.duration || '8'} hours</span>
        </div>
      </div>

      <div className="flex items-center gap-2 mt-4 pt-3 border-t border-gray-100">
        <button
          onClick={() => onEdit(shift)}
          className="flex-1 flex items-center justify-center gap-1 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded transition"
        >
          <Edit2 size={14} />
          Edit
        </button>
        <button
          onClick={() => onDelete(shift.id)}
          className="flex-1 flex items-center justify-center gap-1 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded transition"
        >
          <Trash2 size={14} />
          Delete
        </button>
      </div>
    </div>
  );
};

// Add/Edit Shift Modal
const ShiftModal = ({ shift, onClose, onSave }) => {
  const [formData, setFormData] = useState(shift || {
    name: '',
    code: '',
    startTime: '09:00',
    endTime: '18:00',
    color: 'blue',
    icon: 'Sun',
    active: true,
    duration: 8,
    breakDuration: 60,
  });

  const handleSave = () => {
    if (!formData.name || !formData.code) {
      toast.error('Please fill in all required fields');
      return;
    }
    onSave(formData);
    toast.success(shift ? 'Shift updated successfully' : 'Shift created successfully');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="font-semibold text-gray-900">{shift ? 'Edit Shift' : 'Add New Shift'}</h3>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Shift Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., Morning Shift"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Code *</label>
              <input
                type="text"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                maxLength={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., MS"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
              <input
                type="time"
                value={formData.startTime}
                onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
              <input
                type="time"
                value={formData.endTime}
                onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Break Duration</label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={formData.breakDuration}
                  onChange={(e) => setFormData({ ...formData, breakDuration: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <span className="text-sm text-gray-500">mins</span>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
              <select
                value={formData.color}
                onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="amber">Amber</option>
                <option value="blue">Blue</option>
                <option value="orange">Orange</option>
                <option value="indigo">Indigo</option>
              </select>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={formData.active}
              onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
              id="shiftActive"
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor="shiftActive" className="text-sm text-gray-700">Active</label>
          </div>
        </div>

        <div className="px-5 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition text-sm font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm font-medium flex items-center gap-2"
          >
            <Save size={14} />
            Save Shift
          </button>
        </div>
      </div>
    </div>
  );
};

// Main Settings Component
export default function AttendanceSettings() {
  const { hasPermission } = useAuth();

  const canViewSettings = hasPermission(P.HRM.ATTENDANCE.SCREEN);
  const canAddShift = hasPermission(P.HRM.ATTENDANCE.ADD_SHIFT);
const canEditShift = hasPermission(P.HRM.ATTENDANCE.EDIT_SHIFT);
const canDeleteShift = hasPermission(P.HRM.ATTENDANCE.DELETE_SHIFT);
  if (!canViewSettings) {
  return (
    <div className="p-6 text-center">
      <p className="text-red-500 font-semibold">
        You do not have permission to access Attendance Settings
      </p>
    </div>
  );
}
  // Shift settings
  const [shifts, setShifts] = useState([
    { id: 1, name: 'Morning Shift', code: 'MS', startTime: '06:00', endTime: '14:00', color: 'amber', icon: 'Sunrise', active: true, duration: 8, breakDuration: 60 },
    { id: 2, name: 'Day Shift', code: 'DS', startTime: '09:00', endTime: '18:00', color: 'blue', icon: 'Sun', active: true, duration: 9, breakDuration: 60 },
    { id: 3, name: 'Evening Shift', code: 'ES', startTime: '14:00', endTime: '22:00', color: 'orange', icon: 'Sunset', active: true, duration: 8, breakDuration: 60 },
    { id: 4, name: 'Night Shift', code: 'NS', startTime: '22:00', endTime: '06:00', color: 'indigo', icon: 'Moon', active: false, duration: 8, breakDuration: 60 },
  ]);
  const [editingShift, setEditingShift] = useState(null);
  const [showAddShift, setShowAddShift] = useState(false);

  // Policy settings
  const [policies, setPolicies] = useState({
    gracePeriod: 15,
    halfDayAfter: 120,
    minWorkingHours: 8,
    overtimeAfter: 9,
    weeklyOffDays: 1,
    maxOvertimePerDay: 4,
    autoAbsentAfter: 4,
  });

  // Feature toggles
  const [features, setFeatures] = useState({
    geoFencing: true,
    faceRecognition: false,
    ipRestriction: true,
    mobileCheckIn: true,
    webCheckIn: true,
    biometric: true,
    autoCheckout: false,
    halfDayLeave: true,
    compOff: true,
    overtimeApproval: true,
  });

  // Notification settings
  const [notifications, setNotifications] = useState({
    lateArrival: true,
    absentAlert: true,
    overtimeAlert: true,
    shiftReminder: true,
    regularizationPending: true,
    weeklyReport: false,
  });

  const handleShiftToggle = (id) => {
    setShifts(shifts.map(s => s.id === id ? { ...s, active: !s.active } : s));
    toast.success('Shift status updated');
  };

  const handleShiftDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this shift?')) {
      setShifts(shifts.filter(s => s.id !== id));
      toast.success('Shift deleted');
    }
  };

  const handleShiftSave = (shiftData) => {
    if (shiftData.id) {
      setShifts(shifts.map(s => s.id === shiftData.id ? shiftData : s));
    } else {
      setShifts([...shifts, { ...shiftData, id: Date.now() }]);
    }
  };

  const handleSaveAll = () => {
    toast.success('All settings saved successfully');
  };

  return (
    <div className="space-y-6">
      {/* Shift Timings Section */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-900">Shift Timings</h3>
            <p className="text-xs text-gray-500 mt-0.5">Configure work shifts and their timings</p>
          </div>

          {canAddShift && (
          <button
            onClick={() => setShowAddShift(true)}
            className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm font-medium"
          >
            <Plus size={14} />
            Add Shift
          </button>
          )}
        </div>
        <div className="p-5">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {shifts.map(shift => (
              <ShiftCard
                key={shift.id}
                shift={shift}
                onEdit={canEditShift ? setEditingShift : () => {}}
               onDelete={canDeleteShift ? handleShiftDelete : () => {}}
                onToggle={handleShiftToggle}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Attendance Policies */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-200">
            <h3 className="font-semibold text-gray-900">Attendance Policies</h3>
            <p className="text-xs text-gray-500 mt-0.5">Configure rules and thresholds</p>
          </div>
          <div className="p-4 space-y-3">
            <NumberInput
              label="Grace Period"
              description="Minutes allowed after shift start"
              value={policies.gracePeriod}
              onChange={(val) => setPolicies({ ...policies, gracePeriod: val })}
              min={0}
              max={60}
              suffix="mins"
            />
            <NumberInput
              label="Half Day After"
              description="Late arrival marks half day after"
              value={policies.halfDayAfter}
              onChange={(val) => setPolicies({ ...policies, halfDayAfter: val })}
              min={30}
              max={240}
              suffix="mins"
            />
            <NumberInput
              label="Min Working Hours"
              description="Required for full day attendance"
              value={policies.minWorkingHours}
              onChange={(val) => setPolicies({ ...policies, minWorkingHours: val })}
              min={4}
              max={12}
              suffix="hours"
            />
            <NumberInput
              label="Overtime After"
              description="Hours to start OT calculation"
              value={policies.overtimeAfter}
              onChange={(val) => setPolicies({ ...policies, overtimeAfter: val })}
              min={6}
              max={12}
              suffix="hours"
            />
            <NumberInput
              label="Auto Absent After"
              description="Mark absent if no check-in for hours"
              value={policies.autoAbsentAfter}
              onChange={(val) => setPolicies({ ...policies, autoAbsentAfter: val })}
              min={1}
              max={8}
              suffix="hours"
            />
          </div>
        </div>

        {/* Check-In Methods */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-200">
            <h3 className="font-semibold text-gray-900">Check-In Methods</h3>
            <p className="text-xs text-gray-500 mt-0.5">Enable/disable attendance capture methods</p>
          </div>
          <div className="p-4 space-y-3">
            <ToggleSwitch
              label="Biometric"
              description="Fingerprint/face recognition device"
              enabled={features.biometric}
              onChange={(val) => setFeatures({ ...features, biometric: val })}
            />
            <ToggleSwitch
              label="Mobile App"
              description="Check-in via employee mobile app"
              enabled={features.mobileCheckIn}
              onChange={(val) => setFeatures({ ...features, mobileCheckIn: val })}
            />
            <ToggleSwitch
              label="Web Portal"
              description="Check-in via browser"
              enabled={features.webCheckIn}
              onChange={(val) => setFeatures({ ...features, webCheckIn: val })}
            />
            <ToggleSwitch
              label="Geo-Fencing"
              description="Restrict check-in to office location"
              enabled={features.geoFencing}
              onChange={(val) => setFeatures({ ...features, geoFencing: val })}
            />
            <ToggleSwitch
              label="IP Restriction"
              description="Allow only from office network"
              enabled={features.ipRestriction}
              onChange={(val) => setFeatures({ ...features, ipRestriction: val })}
            />
            <ToggleSwitch
              label="Face Recognition"
              description="AI-powered face verification"
              enabled={features.faceRecognition}
              onChange={(val) => setFeatures({ ...features, faceRecognition: val })}
            />
          </div>
        </div>

        {/* Leave & Overtime Settings */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-200">
            <h3 className="font-semibold text-gray-900">Leave & Overtime</h3>
            <p className="text-xs text-gray-500 mt-0.5">Configure leave and OT rules</p>
          </div>
          <div className="p-4 space-y-3">
            <ToggleSwitch
              label="Half Day Leave"
              description="Allow half-day leave requests"
              enabled={features.halfDayLeave}
              onChange={(val) => setFeatures({ ...features, halfDayLeave: val })}
            />
            <ToggleSwitch
              label="Compensatory Off"
              description="Allow comp-off for extra work"
              enabled={features.compOff}
              onChange={(val) => setFeatures({ ...features, compOff: val })}
            />
            <ToggleSwitch
              label="Overtime Approval"
              description="Require manager approval for OT"
              enabled={features.overtimeApproval}
              onChange={(val) => setFeatures({ ...features, overtimeApproval: val })}
            />
            <ToggleSwitch
              label="Auto Check-Out"
              description="Auto checkout at shift end if missed"
              enabled={features.autoCheckout}
              onChange={(val) => setFeatures({ ...features, autoCheckout: val })}
            />
          </div>
        </div>

        {/* Notifications */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-200">
            <h3 className="font-semibold text-gray-900">Notifications</h3>
            <p className="text-xs text-gray-500 mt-0.5">Configure alert preferences</p>
          </div>
          <div className="p-4 space-y-3">
            <ToggleSwitch
              label="Late Arrival Alert"
              description="Notify managers of late arrivals"
              enabled={notifications.lateArrival}
              onChange={(val) => setNotifications({ ...notifications, lateArrival: val })}
            />
            <ToggleSwitch
              label="Absent Alert"
              description="Alert when employee is absent"
              enabled={notifications.absentAlert}
              onChange={(val) => setNotifications({ ...notifications, absentAlert: val })}
            />
            <ToggleSwitch
              label="Overtime Alert"
              description="Alert when approaching OT limit"
              enabled={notifications.overtimeAlert}
              onChange={(val) => setNotifications({ ...notifications, overtimeAlert: val })}
            />
            <ToggleSwitch
              label="Shift Reminder"
              description="Remind employees before shift"
              enabled={notifications.shiftReminder}
              onChange={(val) => setNotifications({ ...notifications, shiftReminder: val })}
            />
            <ToggleSwitch
              label="Regularization Pending"
              description="Notify of pending approvals"
              enabled={notifications.regularizationPending}
              onChange={(val) => setNotifications({ ...notifications, regularizationPending: val })}
            />
            <ToggleSwitch
              label="Weekly Summary"
              description="Send weekly attendance report"
              enabled={notifications.weeklyReport}
              onChange={(val) => setNotifications({ ...notifications, weeklyReport: val })}
            />
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSaveAll}
          className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium"
        >
          <Save size={16} />
          Save All Settings
        </button>
      </div>

      {/* Modals */}
      {(editingShift || showAddShift) && (
        <ShiftModal
          shift={editingShift}
          onClose={() => {
            setEditingShift(null);
            setShowAddShift(false);
          }}
          onSave={handleShiftSave}
        />
      )}
    </div>
  );
}
